import React, { useState } from 'react';
import { Button, ButtonProps, Tooltip } from '@mantine/core';

export interface BaseActionButtonProps extends Omit<ButtonProps, 'onClick'> {
  // Action configuration
  action: () => Promise<void>;
  loading?: boolean;

  // UI configuration
  tooltip?: string;
  confirmBeforeAction?: boolean;
  confirmMessage?: React.ReactNode;
  confirmTitle?: string;

  // Callbacks
  onSuccess?: (result?: any) => void;
  onError?: (error: Error) => void;

  // Notification configuration
  showNotification?: boolean;
  successNotification?: {
    title: string;
    message: string;
  };
  errorNotification?: {
    title: string;
    message: string;
  };

  // Button content
  children?: React.ReactNode;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
}

export const BaseActionButton: React.FC<BaseActionButtonProps> = ({
  action,
  loading: externalLoading = false,
  tooltip,
  confirmBeforeAction = false,
  confirmMessage,
  confirmTitle,
  onSuccess,
  onError,
  showNotification = true,
  successNotification,
  errorNotification,
  children,
  leftSection,
  rightSection,
  disabled,
  ...buttonProps
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const isButtonLoading = externalLoading || isLoading;

  const handleAction = async () => {
    setIsLoading(true);
    try {
      await action();

      // Show success notification
      if (showNotification && successNotification) {
        import('@mantine/notifications').then(({ notifications }) => {
          notifications.show({
            title: successNotification.title,
            message: successNotification.message,
            color: 'green',
          });
        });
      }

      onSuccess?.();
    } catch (error) {
      console.error('Action failed:', error);

      // Show error notification
      if (showNotification && errorNotification) {
        import('@mantine/notifications').then(({ notifications }) => {
          notifications.show({
            title: errorNotification.title,
            message: errorNotification.message,
            color: 'red',
          });
        });
      }

      onError?.(error instanceof Error ? error : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    if (confirmBeforeAction) {
      import('@mantine/modals').then(({ modals }) => {
        modals.openConfirmModal({
          title: confirmTitle,
          children: confirmMessage,
          labels: { confirm: 'Conferma', cancel: 'Annulla' },
          confirmProps: { color: 'red' },
          onConfirm: handleAction,
        });
      });
    } else {
      handleAction();
    }
  };

  const button = (
    <Button
      onClick={handleClick}
      loading={isButtonLoading}
      disabled={disabled || isButtonLoading}
      leftSection={leftSection}
      rightSection={rightSection}
      {...buttonProps}
    >
      {children}
    </Button>
  );

  if (tooltip) {
    return <Tooltip label={tooltip}>{button}</Tooltip>;
  }

  return button;
};
